import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

interface ThemeContextType {
    themeMode: ThemeMode;
    effectiveTheme: EffectiveTheme;
    setThemeMode: (mode: ThemeMode) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const getSystemTheme = (): EffectiveTheme => {
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
};

const STORAGE_KEY = 'theme_preference';

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'light' || saved === 'dark' || saved === 'system') {
            return saved;
        }
        return 'system';
    });

    const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'light' || saved === 'dark') {
            return saved;
        }
        return getSystemTheme();
    });

    useEffect(() => {
        const updateTheme = () => {
            const resolved = themeMode === 'system' ? getSystemTheme() : themeMode;
            setEffectiveTheme(resolved);
            document.documentElement.setAttribute('data-bs-theme', resolved);
        };

        updateTheme();

        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemChange = () => {
            if (themeMode === 'system') {
                updateTheme();
            }
        };

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleSystemChange);
        } else {
            // Fallback for legacy browsers supporting addListener
            mediaQuery.addListener(handleSystemChange);
        }

        return () => {
            if (typeof mediaQuery.removeEventListener === 'function') {
                mediaQuery.removeEventListener('change', handleSystemChange);
            } else {
                // Fallback for legacy browsers supporting removeListener
                mediaQuery.removeListener(handleSystemChange);
            }
        };
    }, [themeMode]);

    const setThemeMode = useCallback((mode: ThemeMode) => {
        setThemeModeState(mode);
        localStorage.setItem(STORAGE_KEY, mode);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeModeState(prevMode => {
            const currentEffective = prevMode === 'system' ? getSystemTheme() : prevMode;
            const nextMode: ThemeMode = currentEffective === 'dark' ? 'light' : 'dark';
            localStorage.setItem(STORAGE_KEY, nextMode);
            return nextMode;
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ themeMode, effectiveTheme, setThemeMode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
